import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { Check, X, ArrowRight, HelpCircle, Loader2 } from "lucide-react";
import clsx from "clsx";
import { billing, type PricingPlan, type PlanComparison } from "@/lib/api";
import { SITE_URL, CONTACT_EMAIL } from "@/lib/siteConfig";
import { useAuth } from "@/hooks/useAuth";

type CheckVal = boolean | string;

const FAQS = [
  { q: "What counts as a subscriber?", a: "Any contact in your list regardless of status — active, unsubscribed, or bounced. Delete bounced and unsubscribed contacts to stay under your plan limit." },
  { q: "Do all plans include AI features?", a: "Yes. The AI campaign builder, spam scorer, and inbox preview are included on every plan including Free. No add-on required." },
  { q: "Can I import an existing list?", a: "Yes. Import via CSV with email, name, and tag columns. MoreMail runs a free MX validation on every imported address automatically." },
  { q: "How does the warmup pool work?", a: "When you opt in, MoreMail sends small AI-generated emails between pool members and auto-marks them as not-spam via IMAP. This builds real sender reputation with Gmail and Outlook. Zero extra cost." },
  { q: "Is there a free trial?", a: "The Free plan is free forever, no credit card needed. Essentials and Standard plans offer a 14-day free trial before billing starts." },
  { q: "What payment methods are accepted?", a: "Visa, Mastercard, and American Express via Stripe. Annual plans are invoiced." },
  { q: "Can I cancel any time?", a: "Yes. Cancel before your next billing date and you won't be charged. Your account downgrades to Free automatically." },
  { q: "What happens if I exceed my limits?", a: "We'll notify you at 80% and 95% of your send limit. If you exceed, sends are queued until the next billing period or you upgrade." },
];

function CellVal({ v }: { v: CheckVal }) {
  if (v === true)  return <Check size={16} className="text-teal mx-auto" />;
  if (v === false) return <X size={14} className="text-black/20 mx-auto" />;
  return <span className="text-xs font-semibold text-ink">{v}</span>;
}

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [comparison, setComparison] = useState<PlanComparison[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    billing.plans()
      .then((d) => { setPlans(d.plans); setComparison(d.comparison); })
      .catch((err) => { console.error("Plans fetch error:", err); setError(err.message || "Failed to load plans"); })
      .finally(() => setLoading(false));
  }, []);

  const handlePlanClick = async (plan: PricingPlan) => {
    // External links (mailto, etc)
    if (plan.cta_link?.startsWith("mailto:")) {
      window.location.href = plan.cta_link;
      return;
    }
    // Not logged in → register
    if (!user) {
      navigate(plan.cta_link || "/register");
      return;
    }
    // Free plan or already on this plan
    if (!plan.monthly_price || plan.slug === user.plan) return;
    // Trigger Stripe checkout
    setCheckoutLoading(plan.slug);
    setCheckoutError(null);
    try {
      const { url } = await billing.checkout(plan.slug, annual ? "year" : "month");
      window.location.href = url;
    } catch (e: any) {
      setCheckoutError(e.message || "Checkout failed. Please try again.");
      setCheckoutLoading(null);
    }
  };

  const cancelled = searchParams.get("checkout") === "cancelled";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 size={24} className="animate-spin text-slate" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <p className="text-red-500 font-semibold mb-2">Failed to load pricing</p>
        <p className="text-sm text-slate mb-4">{error}</p>
        <button onClick={() => window.location.reload()}
          className="px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark">
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <SeoHead
        title="Pricing — MoreMail | Start Free, Scale As You Grow"
        description="Start free, scale as you grow. All plans include AI campaign builder, warmup pool, email validation, and spintax."
        url={`${SITE_URL}/pricing`}
        breadcrumbs={[{name:"Home",url:SITE_URL},{name:"Pricing",url:`${SITE_URL}/pricing`}]}
      />

      {cancelled && (
        <div className="max-w-3xl mx-auto px-4 pt-6">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl px-5 py-3 text-sm">
            Checkout was cancelled. No charges were made — you can try again any time.
          </div>
        </div>
      )}

      {checkoutError && (
        <div className="max-w-3xl mx-auto px-4 pt-6">
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-2xl px-5 py-3 text-sm flex items-center justify-between">
            <span>Something went wrong starting checkout. Please try again.</span>
            <button onClick={() => setCheckoutError(null)}
              className="ml-4 text-red-400 hover:text-red-600 flex-shrink-0">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-10 sm:pb-14 text-center">
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-ink mb-3">Simple pricing. No surprises.</h1>
        <p className="text-slate text-base sm:text-lg mb-8">Start free. Scale as you grow. Cancel any time.</p>

        {/* Monthly / Annual toggle */}
        <div className="inline-flex items-center gap-3 p-1 bg-black/[0.04] rounded-full">
          <button onClick={() => setAnnual(false)}
            className={clsx("px-4 py-2 rounded-full text-sm font-semibold transition-all",
              !annual ? "bg-white text-ink shadow-sm" : "text-slate hover:text-ink")}>
            Monthly
          </button>
          <button onClick={() => setAnnual(true)}
            className={clsx("px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2",
              annual ? "bg-white text-ink shadow-sm" : "text-slate hover:text-ink")}>
            Annual
            <span className="text-[11px] font-bold px-2 py-0.5 bg-teal/10 text-teal rounded-full">Save 17%</span>
          </button>
        </div>
      </section>

      {/* Plan cards */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
        <div className={clsx("grid grid-cols-1 gap-4", plans.length === 2 && "sm:grid-cols-2", plans.length === 3 && "sm:grid-cols-3", plans.length >= 4 && "sm:grid-cols-2 lg:grid-cols-4")}>
          {plans.map((plan) => {
            const price = plan.monthly_price === null ? null : annual ? plan.annual_price : plan.monthly_price;
            const isPopular = !!plan.is_popular;
            const subsLabel = plan.subscriber_limit ? plan.subscriber_limit.toLocaleString() : "Unlimited";
            const emailsLabel = plan.monthly_email_limit ? `${plan.monthly_email_limit.toLocaleString()}/mo` : "Unlimited";
            return (
              <div key={plan.slug}
                className={clsx(
                  "relative rounded-2xl flex flex-col",
                  isPopular
                    ? "bg-ink text-white shadow-2xl shadow-ink/30 ring-1 ring-white/10"
                    : "bg-white border border-black/[0.06] shadow-sm"
                )}>
                {isPopular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 bg-coral text-white text-[11px] font-bold rounded-full uppercase tracking-wide shadow-md">
                    Most popular
                  </span>
                )}
                <div className="p-6 sm:p-8 flex-1">
                  <p className={clsx("text-xs font-bold uppercase tracking-widest mb-3", isPopular ? "text-white/40" : "text-slate")}>
                    {plan.name}
                  </p>
                  <div className="mb-1">
                    {price === null ? (
                      <span className={clsx("font-display text-4xl font-bold", isPopular ? "text-white" : "text-ink")}>Custom</span>
                    ) : (
                      <>
                        <span className={clsx("font-display text-5xl font-bold", isPopular ? "text-white" : "text-ink")}>${Number(price) % 1 === 0 ? Math.round(Number(price)) : price}</span>
                        <span className={clsx("text-sm ml-1", isPopular ? "text-white/40" : "text-slate")}>/mo</span>
                        {annual && Number(price) > 0 && <span className="text-xs ml-2 text-teal font-semibold">billed annually</span>}
                      </>
                    )}
                  </div>
                  <p className={clsx("text-sm mb-6", isPopular ? "text-white/60" : "text-slate")}>{plan.description}</p>
                  <div className={clsx("grid grid-cols-2 gap-3 p-4 rounded-xl mb-6 text-xs", isPopular ? "bg-white/[0.06]" : "bg-black/[0.02]")}>
                    <div>
                      <p className={isPopular ? "text-white/40" : "text-slate"}>Subscribers</p>
                      <p className={clsx("font-bold mt-0.5", isPopular ? "text-white" : "text-ink")}>{subsLabel}</p>
                    </div>
                    <div>
                      <p className={isPopular ? "text-white/40" : "text-slate"}>Sends</p>
                      <p className={clsx("font-bold mt-0.5", isPopular ? "text-white" : "text-ink")}>{emailsLabel}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handlePlanClick(plan)}
                    disabled={checkoutLoading === plan.slug || (!!user && plan.slug === user.plan)}
                    className={clsx(
                      "flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-sm transition-colors w-full",
                      isPopular
                        ? "bg-coral text-white hover:bg-coral-dark shadow-lg shadow-coral/30"
                        : "border border-black/[0.1] text-ink hover:border-black/[0.2] hover:bg-black/[0.02]",
                      (!!user && plan.slug === user.plan) && "opacity-60 cursor-default"
                    )}>
                    {checkoutLoading === plan.slug ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : user && plan.slug === user.plan ? (
                      "Current plan"
                    ) : (
                      <>{plan.cta_label || "Get started"} <ArrowRight size={14} /></>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust row */}
        <div className="flex flex-wrap justify-center gap-6 mt-8 text-xs text-slate">
          {["14-day free trial on paid plans","No credit card for Free","Cancel any time"].map((t) => (
            <div key={t} className="flex items-center gap-1.5">
              <Check size={13} className="text-teal" /> {t}
            </div>
          ))}
        </div>
      </section>

      {/* Comparison table */}
      {comparison.length > 0 && (
      <section className="bg-white border-y border-black/[0.06]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-8 text-center">Full feature comparison</h2>
          <div className="bg-paper rounded-2xl border border-black/[0.06] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[480px]">
                <thead>
                  <tr className="border-b border-black/[0.06]">
                    <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate">Feature</th>
                    {plans.map((p) => (
                      <th key={p.slug} className={clsx("px-4 py-4 text-center text-xs font-bold uppercase tracking-wider", p.is_popular ? "text-coral" : "text-slate/60")}>
                        {p.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, i) => (
                    <tr key={row.key} className={i % 2 === 0 ? "bg-white" : ""}>
                      <td className="px-5 py-3.5 text-sm text-ink flex items-center gap-1.5">
                        {row.label}
                        {row.tooltip && (
                          <span title={row.tooltip} className="cursor-help">
                            <HelpCircle size={12} className="text-slate/40" />
                          </span>
                        )}
                      </td>
                      {plans.map((p) => (
                        <td key={p.slug} className="px-4 py-3.5 text-center">
                          <CellVal v={row.vals[p.slug] ?? false} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* FAQ */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-8 text-center">Frequently asked questions</h2>
        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <div key={i} className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left">
                <span className="font-semibold text-ink text-sm pr-4">{faq.q}</span>
                <span className={clsx("flex-shrink-0 w-5 h-5 rounded-full bg-black/[0.04] flex items-center justify-center text-slate transition-transform text-xs font-bold", openFaq === i ? "rotate-45" : "")}>
                  +
                </span>
              </button>
              {openFaq === i && (
                <div className="px-5 pb-5">
                  <p className="text-sm text-slate leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
        <div className="bg-ink rounded-2xl p-8 sm:p-10 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-3">Need custom volume or SLAs?</h2>
          <p className="text-white/60 text-sm mb-6">Dedicated IP, custom send limits, SAML SSO, and a named account manager.</p>
          <a href={`mailto:${CONTACT_EMAIL}`}
            className="inline-flex items-center gap-2 px-7 py-3 bg-coral rounded-full text-white font-semibold text-sm hover:bg-coral-dark transition-colors shadow-lg shadow-coral/30">
            Talk to us <ArrowRight size={14} />
          </a>
        </div>
      </section>
    </>
  );
}
